import { useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { VoiceCursor, showCursor, hideCursor, setCursorLabel, moveCursorToElement, cursorClick } from '@/voice/VoiceCursor';
import { getVoiceAction, getActionCatalog } from '@/voice/voiceActions';
import { NeonBorder } from './NeonBorder';

/**
 * Web tarafı köprü: cockpit'ten socket ile gelen aksiyonları mevcut Voice-Action
 * registry + cursor ile çalıştırır, sonucu geri yollar. Agent aktifken neon kenar.
 * Ses/STT yok — tamamen socket komutlarıyla sürülür.
 */
export function AgentBridge() {
  const [active, setActive] = useState(false);
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (!token) return;

    const socket = io(window.location.origin, {
      path: '/agent-socket',
      auth: { token, role: 'web' },
      transports: ['websocket', 'polling'],
    });
    socketRef.current = socket;

    // Cockpit → aksiyon çalıştır
    socket.on('agent:action', async (payload: { reqId?: string; action: string; args?: Record<string, any> }) => {
      const { reqId, action, args } = payload || ({} as any);
      const done = (ok: boolean, result: any) => socket.emit('agent:result', { reqId, ok, result });
      try {
        const a = getVoiceAction(action);
        if (!a) return done(false, `Unbekannte Aktion: ${action}`);
        setCursorLabel(a.description?.slice(0, 48) || action);
        showCursor();
        const target = a.getTarget?.() ?? null;
        if (target) { await moveCursorToElement(target); await cursorClick(); }
        const r = await a.run(args || {});
        done(true, typeof r === 'string' ? r : (r ?? 'ok'));
      } catch (e: any) {
        done(false, e?.message || 'Fehler');
      }
    });

    // Presence: bağlanınca ve ping gelince kendini duyur (cockpit "Desk aktif" görsün)
    socket.on('connect', () => socket.emit('agent:ping', {}));
    // Nur auf Pings des Cockpits antworten — nie auf die eines anderen Web-Tabs
    // (sonst Endlosschleife zwischen zwei Tabs derselben Organisation).
    socket.on('agent:ping', (p?: { from?: string }) => { if (p?.from === 'cockpit') socket.emit('agent:ping', {}); });

    // Cockpit → tool kataloğu iste
    socket.on('agent:catalog:req', () => {
      socket.emit('agent:catalog:res', { catalog: getActionCatalog(), path: window.location.pathname });
    });

    // Cockpit → session başlat/bitir (neon)
    socket.on('agent:session', (p: { active?: boolean }) => {
      const on = !!p?.active;
      setActive(on);
      if (!on) hideCursor();
    });

    socket.on('disconnect', () => { setActive(false); hideCursor(); });

    return () => { socket.disconnect(); socketRef.current = null; };
  }, []);

  return (
    <>
      <VoiceCursor />
      <NeonBorder active={active} />
    </>
  );
}
